import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';
import ModalContentWrapper from 'components/ModalContentWrapper';

import type { GroupConfigDevicesResponseType } from 'redux-store/slices/groupConfigDevices/types';
import type { DeviceSerial } from 'redux-store/slices/ui/types';

import AddDevicesToGroups from './AddDevicesToGroups';
import AddDevicesToGroupsConflict from './AddDevicesToGroupsConflict';
import AddDevicesToGroupsSuccess from './AddDevicesToGroupsSuccess';
import CreateLoadBalancingGroup from './CreateLoadBalancingGroup';
import ProtectedCharger from './ProtectedCharger';

export type AddDevicesToGroupsStage =
  | 'createGroup'
  | 'devices'
  | 'conflict'
  | 'success'
  | 'protected';

interface LoadBalancingDataProps {
  loadBalancingEnabled: boolean;
  loadBalancingGroupId: string;
  maxTotalCurrent: number;
  maxGridCurrent: number;
}
export interface AddLoadBalancingGroupModalState {
  stage: AddDevicesToGroupsStage;
  loadBalancingData?: LoadBalancingDataProps;
  selectedDevices: DeviceSerial[];
  selectedGroup: string | null;
  configureGroup: boolean;
  conflictingSerials?: DeviceSerial[];
  deviceIds: number[];
  groupIds: number[];
  serial?: string;
  currentTab: 'devices' | 'groups';
  pendingDevices: GroupConfigDevicesResponseType[];
}

export interface AddLoadBalancingGroupModalStageProps {
  open: boolean;
  state: AddLoadBalancingGroupModalState;
  setState: Dispatch<SetStateAction<AddLoadBalancingGroupModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  selectedDevices: DeviceSerial[];
  selectedGroup: string | null;
  setSelectedDevices?: (devices: string[]) => void;
}

const StageComponents: Record<
  AddDevicesToGroupsStage,
  FC<AddLoadBalancingGroupModalStageProps>
> = {
  createGroup: CreateLoadBalancingGroup,
  devices: AddDevicesToGroups,
  conflict: AddDevicesToGroupsConflict,
  success: AddDevicesToGroupsSuccess,
  protected: ProtectedCharger,
};

interface Props extends DefaultPopupProps {
  selectedDevices: DeviceSerial[];
  selectedGroup: string | null;
  configureGroup: boolean;
  maxTotalCurrent?: number;
  maxGridCurrent?: number;
  setSelectedDevices?: (devices: string[]) => void;
}
export const initialState: AddLoadBalancingGroupModalState = {
  stage: 'createGroup',
  loadBalancingData: undefined,
  selectedDevices: [],
  selectedGroup: null,
  configureGroup: false,
  conflictingSerials: [],
  deviceIds: [],
  groupIds: [],
  serial: undefined,
  currentTab: 'devices',
  pendingDevices: [],
};

const AddLoadBalancingGroupModal: FC<Props> = ({
  onClose,
  open,
  selectedDevices,
  selectedGroup,
  maxTotalCurrent,
  maxGridCurrent,
  configureGroup,
  setSelectedDevices,
}) => {
  const { t } = useTranslation();

  const [state, setState] =
    useState<AddLoadBalancingGroupModalState>(initialState);

  useEffect(() => {
    if (open) {
      setState({
        stage:
          configureGroup === false && selectedGroup ? 'devices' : 'createGroup',
        loadBalancingData: {
          loadBalancingEnabled: true,
          loadBalancingGroupId: selectedGroup !== null ? selectedGroup : '',
          maxTotalCurrent: maxTotalCurrent ?? 32,
          maxGridCurrent: maxGridCurrent ?? 32,
        },
        selectedDevices: selectedDevices,
        selectedGroup: selectedGroup,
        configureGroup: configureGroup,
        conflictingSerials: [],
        deviceIds: [],
        groupIds: [],
        serial: undefined,
        currentTab: 'devices',
        pendingDevices: [],
      });
    }
  }, [
    configureGroup,
    maxGridCurrent,
    maxTotalCurrent,
    open,
    selectedDevices,
    selectedGroup,
  ]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (
      state.stage === 'createGroup' ||
      selectedGroup ||
      state.stage === 'success'
    ) {
      setState(initialState);
      onClose();
    } else if (state.stage === 'devices') {
      setState(prev => ({ ...prev, stage: 'createGroup' }));
    } else if (state.stage === 'protected') {
      setState(prev => ({ ...prev, stage: 'success' }));
    } else {
      setState(prev => ({ ...prev, stage: 'devices', conflictingSerials: [] }));
    }
  }, [onClose, selectedGroup, state.stage]);

  const headerTitle = useMemo((): string | undefined => {
    if (state.stage === 'protected') {
      return t('common:pages.load_balancing.add_load_balancing_group.pending');
    }

    if (state.stage === 'createGroup' && !selectedGroup) {
      return t('common:pages.load_balancing.add_load_balancing_group.title');
    }

    return undefined;
  }, [selectedGroup, state.stage, t]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'createGroup' &&
        selectedGroup === null &&
        state.stage !== 'success' ? (
          <ArrowBackRoundedIcon />
        ) : undefined
      }
      header={headerTitle}
    >
      <ModalContentWrapper>
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          selectedDevices={selectedDevices}
          selectedGroup={selectedGroup}
          setSelectedDevices={setSelectedDevices}
        />
      </ModalContentWrapper>
    </Modal>
  );
};

export default AddLoadBalancingGroupModal;
